import { useCallback } from 'react';

import { useTranslation } from '@kc-console/core';

import { InlinePropertyFilter, SeparatedPropertyFilter } from '../PropertyFilter';
import { useFilterUtils } from './useFilterUtils';

import type { ReactNode } from 'react';
import type { FilterState, PropertyFilterDef } from '../DataTable/types';
import type { PropertyFilterType } from './utils';

export interface ActionBarPropertyFilterProps {
  filterDef: PropertyFilterDef;
  filters: FilterState[];
  onSubmit: (params: { filterKey: string; filterValue: unknown }) => void;
  autoFocus?: boolean;
  disabled?: boolean;
  type?: PropertyFilterType;
}

export default function ActionBarPropertyFilter(props: ActionBarPropertyFilterProps) {
  const { t } = useTranslation();
  const { autoFocus, filterDef, filters, onSubmit, type = 'separated', disabled = false } = props;

  const {
    filterKey,
    onFilterKeyChange,
    filterKeyOption,
    filterKeySelectOptions,
    filterValue,
    onFilterValueChange,
    filterValueSelectOptions,
    isFilterValueSelectOptionsLoading,
  } = useFilterUtils({
    filterDef,
    filterIndex: null,
    filters,
    defaultValues: {
      filterKey: type === 'inline' ? null : undefined,
    },
  });

  const handleSubmit = useCallback(
    (params: { filterKey: string; filterValue: unknown }) => {
      const { filterKey, filterValue } = params;
      if (filterKeyOption?.type === 'select') {
        const matchedOption = filterValueSelectOptions.find(
          filterValueOption => filterValueOption.text === filterValue,
        );
        if (!matchedOption) return;
        onSubmit({ filterKey, filterValue: matchedOption.value });
        return;
      }
      onSubmit({ filterKey, filterValue });
      onFilterValueChange('');
    },
    [onSubmit, filterKeyOption, onFilterValueChange, filterValueSelectOptions],
  );

  return (
    {
      inline: () => (
        <InlinePropertyFilter
          autoFocus={autoFocus}
          filterKey={filterKey}
          filterKeyOptions={filterKeySelectOptions}
          filterValue={typeof filterValue === 'string' ? filterValue : ''}
          placeholder={
            filterKey
              ? ''
              : (filterDef.placeholder ?? t('common:common.general.enter_property_or_value'))
          }
          filterValueOptions={filterValueSelectOptions}
          isFilterValueOptionsLoading={isFilterValueSelectOptionsLoading}
          onFilterKeyChange={onFilterKeyChange}
          onFilterValueChange={onFilterValueChange}
          onSubmit={handleSubmit}
          disabled={disabled}
        />
      ),
      separated: () => (
        <SeparatedPropertyFilter
          autoFocus={autoFocus}
          filterKey={filterKey}
          filterKeyOptions={filterKeySelectOptions}
          filterKeyDefaultOption={filterDef.defaultOption}
          filterValue={typeof filterValue === 'string' ? filterValue : ''}
          placeholder={filterDef.placeholder ?? t('common:common.prompt.enter_value')}
          filterValueOptions={filterValueSelectOptions}
          isFilterValueOptionsLoading={isFilterValueSelectOptionsLoading}
          onFilterKeyChange={onFilterKeyChange}
          onFilterValueChange={onFilterValueChange}
          onSubmit={handleSubmit}
          disabled={disabled}
        />
      ),
    } as Record<Required<ActionBarPropertyFilterProps>['type'], () => ReactNode>
  )[type]();
}
